import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { IVisibilityCandidate } from "./IVisibilityCandidate";
import { ProfileRule } from "./ProfileRule";

export enum VisibilityProfileType {
  TRADER,
  SALES,
}

export const INBOX_ORDER_STATES: Set<CubiqTS.OrderState> = new Set([
  CubiqTS.OrderState.ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED,
]);

/**
 * Set of active ordered order states - also used by ActiveUser.canSeeOrderInBlotter()
 */
export const ACTIVE_ORDER_STATES: Set<CubiqTS.OrderState> = new Set([
  CubiqTS.OrderState.ACTIVE,
  CubiqTS.OrderState.TRIGGERED,
  CubiqTS.OrderState.MANUAL_PRICING,
  CubiqTS.OrderState.ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.PENDING,
  CubiqTS.OrderState.PENDING_CANCELLATION,
  CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.IN_MODIFICATION,
  CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.SUSPENDED,
  CubiqTS.OrderState.FILLING,
  CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.FILL_PENDING,
  CubiqTS.OrderState.PENDING_FWDS,
  CubiqTS.OrderState.CANCEL_PENDING_FWDS,
  CubiqTS.OrderState.EXPIRY_PENDING_FWDS,
]);

/**
 * Set of states that are monitored - we now also monitoring GAMMA_MID_PENDING orders so we can provide details on them
 * in Gamma tooltips
 */
export const MONITORED_ORDER_STATES: Set<CubiqTS.OrderState> = new Set(ACTIVE_ORDER_STATES).add(
  CubiqTS.OrderState.GAMMA_MID_PENDING,
);

/**
 * Set of states that are monitored for algos - also used by ActiveUser.canSeeOrderInBlotter()
 */
export const ALGO_MONITORED_ORDER_STATES: Set<CubiqTS.OrderState> = new Set(ACTIVE_ORDER_STATES)
  .add(CubiqTS.OrderState.CANCELLED)
  .add(CubiqTS.OrderState.EXPIRED)
  .add(CubiqTS.OrderState.REJECTED)
  .add(CubiqTS.OrderState.FILLED)
  .add(CubiqTS.OrderState.DI_NEEDED);

/**
 * Checks if the order state of a given order should be monitored in the app based
 * on the order state of the order.
 *
 * @param order the order to check
 * @returns true if the order should be monitored, false otherwise
 */
export function checkMonitoredOrderState(order: CubiqTS.OrderDataImpl): boolean {
  if (!order) {
    return false;
  }
  let stateList: Set<CubiqTS.OrderState>;
  switch (order.orderType) {
    case CubiqTS.OrderType.ALGO:
    case CubiqTS.OrderType.BMALGO:
    case CubiqTS.OrderType.LIMIT_TP:
    case CubiqTS.OrderType.LIMIT_SL:
    case CubiqTS.OrderType.LIMIT_CALL:
    case CubiqTS.OrderType.MARKET:
    case CubiqTS.OrderType.MARKET_IOC:
    case CubiqTS.OrderType.MARKET_FOK:
      if (order.gammaRungId !== null) {
        stateList = MONITORED_ORDER_STATES;
      } else {
        stateList = ALGO_MONITORED_ORDER_STATES;
      }
      break;
    default:
      stateList = MONITORED_ORDER_STATES;
      break;
  }

  let visible = stateList.has(order.orderState);

  //Remove anything in DI_NEEDED that's not an Algo in modification
  if (
    order.orderState === CubiqTS.OrderState.DI_NEEDED &&
    visible &&
    ((order.orderType !== CubiqTS.OrderType.ALGO && order.orderType !== CubiqTS.OrderType.BMALGO) ||
      order.lockType !== CubiqTS.LockType.MODIFY_LOCK)
  ) {
    visible = false;
  }
  return visible;
}

export const BENCHMARK_ACTIVE_STATES: Set<CubiqTS.OrderState> = new Set([
  CubiqTS.OrderState.ACTIVE,
  CubiqTS.OrderState.TRIGGERED,
  CubiqTS.OrderState.PRICING,
  CubiqTS.OrderState.ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.PENDING,
  CubiqTS.OrderState.PENDING_CANCELLATION,
  CubiqTS.OrderState.IN_MODIFICATION,
  CubiqTS.OrderState.MATCHED,
  CubiqTS.OrderState.SAVED,
  CubiqTS.OrderState.FILLED,
  CubiqTS.OrderState.PRICED,
  CubiqTS.OrderState.FILL_PENDING,
  CubiqTS.OrderState.PENDING_FWDS,

  // In Modification
  // CubiqTS.OrderState.DI_NEEDED,
]);

// used for fixings update
export const BENCHMARK_REJECT_STATES: Set<CubiqTS.OrderState> = new Set([
  CubiqTS.OrderState.REJECTED,
  CubiqTS.OrderState.CANCELLED,
  CubiqTS.OrderState.CLIENT_REJECTED,
  CubiqTS.OrderState.TRADER_REJECTED,
  CubiqTS.OrderState.DELETED,
  CubiqTS.OrderState.SALES_REJECTED,
]);

export const HEDGE_ORDER_ACTIVE_STATES: Set<CubiqTS.OrderState> = new Set([
  CubiqTS.OrderState.ACTIVE,
  CubiqTS.OrderState.TRIGGERED,
  CubiqTS.OrderState.PRICING,
  CubiqTS.OrderState.ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.PENDING,
  CubiqTS.OrderState.PENDING_CANCELLATION,
  CubiqTS.OrderState.IN_MODIFICATION,
  CubiqTS.OrderState.FILLED,
  CubiqTS.OrderState.MATCHED,
  CubiqTS.OrderState.SAVED,
  CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.CANCELLED,
  CubiqTS.OrderState.REJECTED,
  CubiqTS.OrderState.EXPIRED,
]);

// used for fixings update
export const HEDGE_ORDER_REJECT_STATES: Set<CubiqTS.OrderState> = new Set([
  CubiqTS.OrderState.DELETED,
]);

abstract class AbstractCandidate implements IVisibilityCandidate {
  lockedToMe: boolean;
  branchID: string;
  entryUserID: string;
  orderType: CubiqTS.OrderType;
  pairID: string;
  salesGroupID: string;
  traderGroupID: string;
  targetGroupID: string;
  interventionState: boolean;
  isOpsInt: boolean;
  // Relevant only for BENCHMARKs
  isBenchmark: boolean;
  bmarkCheckAllocationOnly: boolean;
  // BMARK single allocation values
  bmarkAllocationTraderGroupID: string;
  bmarkAllocationCcyPair: string;
  // BMARK order data values
  bmarkTraderGroupIDs: Array<string>;
  bmarkCcyPairs: Array<string>;
  bmarkComponentIds: Array<string>;
}

export class OrderDataCandidate extends AbstractCandidate implements IVisibilityCandidate {
  constructor(
    orderData: CubiqTS.OrderDataImpl,
    includeTarget: boolean = true,
    myUserId: string = null,
    allocationData: CubiqTS.AllocationDataImpl = null,
    opsIntEvent: CubiqTS.OpsIntEvent = null,
  ) {
    super();
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      this.isBenchmark = orderData.orderType === CubiqTS.OrderType.BENCHMARK;
      this.bmarkCheckAllocationOnly =
        this.isBenchmark && allocationData instanceof CubiqTS.AllocationDataImpl;
      // && allocationData.allocationType !== CubiqTS.AllocationType.CROSS; // check everything for

      if (orderData.lockUserId && myUserId) {
        // if the order is locked to me I can see it - trumps the rest
        this.lockedToMe = orderData.lockUserId.toString() === myUserId;
      }

      this.branchID = orderData.clientBranchCode;
      this.entryUserID = orderData.entryUserId.toString();
      this.orderType = orderData.orderType;
      this.pairID = orderData.baseCode + orderData.nonBaseCode;
      this.salesGroupID = orderData.salesGroupId ? orderData.salesGroupId.toString() : null;

      // blotter visibility should discount target group
      // included by default for global visibility
      if (includeTarget) {
        this.targetGroupID = orderData.targetDeskId ? orderData.targetDeskId.toString() : null;
      }
      this.interventionState = INBOX_ORDER_STATES.has(orderData.orderState);
      this.isOpsInt = opsIntEvent != null && opsIntEvent instanceof CubiqTS.OpsIntEventImpl;
      if (this.isBenchmark) {
        if (this.bmarkCheckAllocationOnly) {
          if (allocationData instanceof CubiqTS.AllocationDataImpl) {
            this.bmarkAllocationTraderGroupID =
              allocationData.deskId instanceof CubiqTS.Long
                ? allocationData.deskId.toString()
                : null;
            this.bmarkAllocationCcyPair = allocationData.baseCcy + allocationData.nonbaseCcy;
            //  logger.out.info(`BMARK: Visibility: AllocationData => [${allocationData.allocationId}-${CubiqTS.AllocationType[allocationData.allocationType]}] = CCY: ${this.bmarkAllocationCcyPair} | TG: ${this.bmarkAllocationTraderGroupID}  `, );
          } else if (this.isOpsInt) {
            this.traderGroupID = orderData.deskId ? orderData.deskId.toString() : null;
          } else {
            logger.out.error(
              `BMARK: Visibility: Allocation Data missing for ${orderData.orderId} single order`,
            );
          }
        } else {
          if (
            Array.isArray(orderData.allocationData) &&
            orderData.allocationData[0] instanceof CubiqTS.AllocationDataImpl
          ) {
            this.bmarkTraderGroupIDs = [];
            this.bmarkCcyPairs = [];
            this.bmarkComponentIds = [];

            if (orderData.allocationData.length > 0) {
              for (let allocationItem of orderData.allocationData) {
                this.processAllocation(allocationItem);
              }
            }
          } else if (this.isOpsInt) {
            // Ops Int Event derrived orders have a different visbility check in ProfileRule
            this.traderGroupID = orderData.deskId ? orderData.deskId.toString() : null;
          } else {
            logger.out.error(`BMARK: Visibility: Allocation Data missing for ${orderData.orderId}`);
          }
        }
      } else {
        this.traderGroupID = orderData.deskId ? orderData.deskId.toString() : null;
      }
    }
  }

  private processAllocation(allocationData: CubiqTS.AllocationDataImpl): void {
    if (allocationData instanceof CubiqTS.AllocationDataImpl) {
      // trader group
      let aloTraderGroup =
        allocationData.deskId instanceof CubiqTS.Long ? allocationData.deskId.toString() : null;
      if (aloTraderGroup != null && !this.bmarkTraderGroupIDs.includes(aloTraderGroup)) {
        this.bmarkTraderGroupIDs.push(aloTraderGroup);
      }
      // ccy-pairs
      let aloCcyPair =
        typeof allocationData.baseCcy === "string" && typeof allocationData.nonbaseCcy === "string"
          ? allocationData.baseCcy + allocationData.nonbaseCcy
          : null;
      if (
        typeof aloCcyPair === "string" &&
        aloCcyPair.length === 6 &&
        !this.bmarkCcyPairs.includes(aloCcyPair)
      ) {
        this.bmarkCcyPairs.push(aloCcyPair);
      }

      let componentIds =
        allocationData.allocationId instanceof CubiqTS.Long
          ? allocationData.allocationId.toString()
          : null;
      if (typeof aloCcyPair === "string") {
        this.bmarkComponentIds.push(componentIds);
      }
      // children allocations
      if (
        Array.isArray(allocationData.componentAllocations) &&
        allocationData.componentAllocations.length > 0 &&
        allocationData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        if (allocationData.componentAllocations.length > 0) {
          for (let allocationItem of allocationData.componentAllocations) {
            this.processAllocation(allocationItem);
          }
        }
      }
    }
  }
}

export class DealCandidate extends AbstractCandidate implements IVisibilityCandidate {
  constructor(
    deal: CubiqTS.DealImpl,
    myUserId: string = null,
    opsIntEvent: CubiqTS.OpsIntEventImpl,
  ) {
    super();
    this.isOpsInt = true;

    if (deal instanceof CubiqTS.DealImpl) {
      this.branchID = deal.clientBranchCd;
      if (CubiqTS.Long.isLong(deal.entryUserId)) {
        this.entryUserID = deal.entryUserId.toString();
      }
      this.orderType = deal.orderType;
      if (typeof deal.baseCcyCd === "string" && typeof deal.nonBaseCcyCd === "string") {
        this.pairID = deal.baseCcyCd + deal.nonBaseCcyCd;
      }
      this.salesGroupID = CubiqTS.Long.isLong(deal.salesGroupId)
        ? deal.salesGroupId.toString()
        : null;
      this.traderGroupID = CubiqTS.Long.isLong(deal.traderGroupId)
        ? deal.traderGroupId.toString()
        : null;
    }
  }
}

export class OpsIntCandidate extends AbstractCandidate implements IVisibilityCandidate {
  constructor(event: CubiqTS.OpsIntEventImpl, myUserId: string = null) {
    super();
    this.isOpsInt = true;

    // TODO return either an order or deal based candidate
    if (
      event != null &&
      event instanceof CubiqTS.OpsIntEventImpl &&
      event.info != null &&
      event.info instanceof CubiqTS.OpsExcInfoImpl &&
      event.info.deal != null &&
      event.info.deal instanceof CubiqTS.DealImpl
    ) {
      return new DealCandidate(event.info.deal, myUserId, event);
    } else if (
      event != null &&
      event instanceof CubiqTS.OpsIntEventImpl &&
      event.info != null &&
      event.info instanceof CubiqTS.OpsExcInfoImpl &&
      event.info.orderData != null &&
      event.info.orderData instanceof CubiqTS.OrderDataImpl
    ) {
      let includeTarget = true;
      return new OrderDataCandidate(event.info.orderData, includeTarget, myUserId, null, event);
    }

    // global type, and others any lacking information will be visible
    return this;
  }
}

export class VisibilityModel {
  private myRules: Array<ProfileRule>;

  private myProfile: CubiqTS.UserVisibilityProfileImpl;

  constructor(profile: CubiqTS.UserVisibilityProfileImpl, userTraderGroups: Array<string>) {
    this.myProfile = profile;
    this.myRules = this.createRules(profile, userTraderGroups);
  }

  private createRules(
    profile: CubiqTS.UserVisibilityProfileImpl,
    userTraderGroups: Array<string>,
  ): Array<ProfileRule> {
    if (profile) {
      const retRules: Array<ProfileRule> = [];

      const isTrader: boolean = VisibilityProfileType.TRADER == profile.visibilityProfileType;
      this.myProfile.userVisibilityRules.forEach((rule) => {
        let profileRule: ProfileRule = new ProfileRule(rule, isTrader, userTraderGroups);
        retRules.push(profileRule);
      });

      return retRules;
    }
    return null;
  }

  public canSeeOpsInt(opsInt: CubiqTS.OpsIntEvent, myUserId: string): boolean {
    const candidate: IVisibilityCandidate = new OpsIntCandidate(opsInt, myUserId);

    if (candidate.entryUserID == myUserId) {
      // Entry User check deferred until candidate available, unlike other object types.
      return true;
    }

    return this.checkRules(candidate);
  }

  public canSeeOrder(order: CubiqTS.OrderDataImpl, myUserId: string): boolean {
    const candidate: OrderDataCandidate = new OrderDataCandidate(order, true, myUserId);
    return this.checkRules(candidate);
  }

  public canSeeOrderInBlotter(order: CubiqTS.OrderDataImpl, myUserId: string): boolean {
    const candidate: OrderDataCandidate = new OrderDataCandidate(order, false, myUserId);
    return this.checkRules(candidate);
  }

  public canSeeOrderInInbox(order: CubiqTS.OrderDataImpl, myUserId: string): boolean {
    const candidate: OrderDataCandidate = new OrderDataCandidate(order, true, myUserId);
    return this.checkRules(candidate, true);
  }

  public canSeeCcyPair(ccyPair: string) {
    for (let i = 0, len = this.myRules.length; i < len; i++) {
      let rule: ProfileRule = this.myRules[i] as ProfileRule;
      if (rule.isCcyPairVisible(ccyPair)) {
        return true;
      }
    }
    return false;
  }

  public canSeeBmarkOrderInIntervention(
    order: CubiqTS.OrderDataImpl,
    myUserId: string,
    tradingGroupsIdSet: Set<string>,
  ): boolean {
    const candidate: OrderDataCandidate = new OrderDataCandidate(order, false, myUserId);
    // used for Bmark
    let belongsToTG = false;
    if (
      Array.isArray(candidate.bmarkTraderGroupIDs) &&
      candidate.bmarkTraderGroupIDs.length > 0 &&
      tradingGroupsIdSet instanceof Set &&
      tradingGroupsIdSet.size > 0
    ) {
      for (let traderGroupId of candidate.bmarkTraderGroupIDs) {
        belongsToTG = belongsToTG || tradingGroupsIdSet.has(traderGroupId);
        if (belongsToTG) break;
      }
    }

    return belongsToTG ? this.checkRules(candidate) : false;
  }

  public canSeeBmarkOrder(order: CubiqTS.OrderDataImpl, myUserId: string): boolean {
    const candidate: OrderDataCandidate = new OrderDataCandidate(order, false, myUserId);
    return this.checkRules(candidate);
  }

  public canSeeBmarkAllocation(
    order: CubiqTS.OrderDataImpl,
    myUserId: string,
    allocationData: CubiqTS.AllocationDataImpl,
  ): boolean {
    const candidate: OrderDataCandidate = new OrderDataCandidate(
      order,
      false,
      myUserId,
      allocationData,
    );
    return this.checkRules(candidate);
  }

  protected checkRules(candidate: AbstractCandidate, inboxOnly: boolean = false): boolean {
    if (this.myRules) {
      for (let i = 0, len = this.myRules.length; i < len; i++) {
        let rule: ProfileRule = this.myRules[i] as ProfileRule;
        if (inboxOnly) {
          if (rule.isInboxVisible(candidate)) {
            return true;
          }
        } else if (candidate.isOpsInt) {
          if (rule.isOpsIntVisible(candidate)) {
            return true;
          }
        } else if (candidate.isBenchmark) {
          if (rule.isBmarkVisible(candidate)) {
            return true;
          }
        } else {
          if (rule.isVisible(candidate)) {
            return true;
          }
        }
      }
    }
    return false;
  }
}
